/**
 * About Page
 */
import { Link } from "react-router-dom";
import { ArrowLeft, Heart, Globe, Users, Sparkles } from "lucide-react";
import Footer from "@/components/Footer";

export default function About() {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background border-b border-primary/10">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link to="/" className="inline-flex items-center text-primary hover:text-primary/80 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 className="text-4xl md:text-5xl font-serif font-semibold text-charcoal mb-4">
            About DuoLyst
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl">
            We believe the best adventures are shared. DuoLyst helps couples, friends, and 
            groups dream together and turn those dreams into reality.
          </p>
        </div>
      </div>

      {/* Content */}
      <main className="flex-1">
        {/* Mission */}
        <section className="max-w-4xl mx-auto px-4 py-16">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-2xl font-serif font-semibold text-charcoal mb-4">Our Mission</h2>
              <p className="text-muted-foreground mb-4">
                Life is short, and the world is wide. We created DuoLyst to help people capture 
                their wildest travel dreams and actually make them happen—together.
              </p>
              <p className="text-muted-foreground">
                Whether it's watching the northern lights with your partner, backpacking through 
                Southeast Asia with friends, or taking that dream family trip to Disney, DuoLyst 
                keeps everyone aligned and excited.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-primary/5 rounded-2xl p-6 text-center">
                <Globe className="w-8 h-8 text-primary mx-auto mb-2" />
                <div className="text-2xl font-bold text-charcoal">195</div>
                <div className="text-sm text-muted-foreground">Countries to explore</div>
              </div>
              <div className="bg-secondary/5 rounded-2xl p-6 text-center">
                <Users className="w-8 h-8 text-secondary mx-auto mb-2" />
                <div className="text-2xl font-bold text-charcoal">∞</div>
                <div className="text-sm text-muted-foreground">Dreams to share</div>
              </div>
              <div className="bg-accent/5 rounded-2xl p-6 text-center">
                <Heart className="w-8 h-8 text-rose-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-charcoal">1</div>
                <div className="text-sm text-muted-foreground">Life to live fully</div>
              </div>
              <div className="bg-primary/5 rounded-2xl p-6 text-center">
                <Sparkles className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                <div className="text-2xl font-bold text-charcoal">AI</div>
                <div className="text-sm text-muted-foreground">Powered suggestions</div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="bg-muted/50 dark:bg-muted/20 py-16">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-2xl font-serif font-semibold text-foreground mb-8 text-center">
              What Makes DuoLyst Special
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-card dark:bg-card rounded-2xl p-6 shadow-sm border border-border">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">Shared Dreams</h3>
                <p className="text-sm text-muted-foreground">
                  Link with your partner or create groups. Everyone sees updates in real-time.
                </p>
              </div>
              <div className="bg-card dark:bg-card rounded-2xl p-6 shadow-sm border border-border">
                <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="w-6 h-6 text-secondary" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">World Map</h3>
                <p className="text-sm text-muted-foreground">
                  Watch your map fill with colors as you check off countries and destinations.
                </p>
              </div>
              <div className="bg-card dark:bg-card rounded-2xl p-6 shadow-sm border border-border">
                <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-amber-500" />
                </div>
                <h3 className="font-semibold text-foreground mb-2">AI Suggestions</h3>
                <p className="text-sm text-muted-foreground">
                  Get personalized trip ideas powered by GPT based on your preferences.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-serif font-semibold text-charcoal mb-4">
            Ready to start dreaming?
          </h2>
          <p className="text-muted-foreground mb-6">
            Join thousands of dreamers who are turning bucket lists into memories.
          </p>
          <Link 
            to="/"
            className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-full font-medium hover:bg-primary/90 transition-colors"
          >
            Get Started Free
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
